// React import
import React, { useMemo, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/server';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Material UI import
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import AlertTitle from '@mui/material/AlertTitle';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { blue, purple, green, yellow } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// FontAwesome import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBill, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faPaperPlane, faHatWizard, faClipboard, faPlus, faEnvelope, faChevronDown, faChevronRight } from '@fortawesome/pro-light-svg-icons';

// Local import
import SendNewEmailDialog from '../components/SendNewEmailDialog.js';
import { getProductDescription } from '../utils/product.js';
import { minimizeProducts } from '../utils/opportunity.js';
import PRODUCT_TYPES from '../../ProductTypes.js';
import email from '../utils/email';
import SidePaneRfqsDrawer from './SidePane.rfqs.drawer.js';
import EmailConversation from '../emailpage/EmailPage.conversation';
import { settleRoute } from '../utils/routes.js';
import Tooltip from '@mui/material/Tooltip';
import SidePaneRfqsSupplierSelection from './SidePane.rfqs.supplierselection.js';
import { ProductsSummary } from '../../utils/ProductHelperComponents.js';
import readableId from '../../ReadableId.js';
import { useEffectEvent } from 'react';
import { getEmailClosing } from '../opportunities/OpportunityOneOffEmailBuilder.utils.js';
import ApiClipboardDialog, { apiOfferToRates, RouteSelector } from './SidePane.clipboarddialog.js';

function SidePaneRfqs(props) {
    const { suppliers, organization, accesstoken, me, emailLoginInfo, onEmailRefreshConnection, onEmailLogout, 
        onUpdateOpportunity, unlocode, seaPorts, exchangeRates, opportunity, setMinimizedSearch, accounts } = props;
    const [ screen, setScreen ] = useState(null);
    const [ conversations, setConversations ] = useState({});
    const [ gettingInfo, setGettingInfo ] = useState(true);
    const [ selectedSuppliers, setSelectedSuppliers ] = useState([]);
    const [ selectedRoute, setSelectedRoute ] = useState(null);
    const [ rateMoreInfo, setRateMoreInfo ] = useState({ open: false });
    const [ internalRequests, setInternalRequests ] = useState([]);
    const [ waitingForConversations, setWaitingForConversations ] = useState(false);
    const [ cacheBody, setCacheBody ] = useState(null);
    const [ cacheSubject, setCacheSubject ] = useState(null);
    // Success toast shown after an RFQ email is sent — the newly created RFQ card is
    // appended below and usually scrolled out of view, so confirm the send in-place.
    const [ sentToast, setSentToast ] = useState(null);
    const [ error, setError ] = useState(false);
    const [ refreshTick, setRefreshTick ] = useState(0);
    // Per-supplier expand state for RFQs carried over from a duplicated opportunity
    const [ expandedCarriedOver, setExpandedCarriedOver ] = useState({});
    // Id of the free-text rate currently being re-parsed (drawer spinner)
    const [ reparsingFreeTextId, setReparsingFreeTextId ] = useState(null);
    // Banner shown in the reply drawer after a re-parse: { severity, message }
    const [ reparseNotice, setReparseNotice ] = useState(null);
    // Conversation whose re-parse we're awaiting, to flip the banner to "saved" once the poll refreshes it
    const reparseAwaitingRef = useRef(null);
    const theme = useTheme();

    const requests = useMemo(() => opportunity?.rateRequests || [], [opportunity]);
    const routes = useMemo(() => opportunity?.routes, [opportunity]);
    const transportationMethod = opportunity?.transportationMethod;
    const products = opportunity?.products;
    const incoterm = opportunity?.incoterm;

    const buyingRates = opportunity?.buyingRates || [];

    function setBuyingRates(func) {
        onUpdateOpportunity({ 
            opportunityId: opportunity.id,
            func: o => {
                const newBuyingRates = func(o.buyingRates);
                return { ...o, buyingRates: newBuyingRates };
        } });
    }

    useEffect(() => {
        setInternalRequests(requests);
    }, [requests]);

    // Poll for pending rate request statuses
    useEffect(() => {
        if (!organization?.integrations?.email?.enabled || !accesstoken) return;

        const pendingRequests = internalRequests.filter(r => r.attributes?.status === 'pending' && r.attributes?.pendingId);

        console.log('[SidePane.rfqs.js] Found the followwing pending requests:', pendingRequests);
        if (pendingRequests.length === 0) return;

        const checkPendingStatuses = async () => {
            let hasUpdates = false;
            const updatedRequests = await Promise.all(internalRequests.map(async (r) => {
                if (r.attributes?.status !== 'pending' || !r.attributes?.pendingId) {
                    return r;
                }

                try {
                    const status = await email.getEmailStatus({
                        org: organization,
                        emailId: r.attributes.pendingId,
                        monadaAccesstoken: accesstoken
                    });

                    console.log('[SidePane.rfq.js] Result for query email status:', status);

                    if (status.status !== 'pending' && status.status !== 'processing') {
                        hasUpdates = true;
                        return {
                            ...r,
                            attributes: {
                                ...r.attributes,
                                status: status.status,
                                ...(status.conversationId ? { conversationId: status.conversationId } : {}),
                                ...(status.internetMessageId ? { internetMessageId: status.internetMessageId } : {}),
                                ...(status.error ? { error: status.error } : {})
                            }
                        };
                    }
                } catch (e) {
                    console.error('[SidePaneRfqs] Error checking pending status for', r.attributes.pendingId, e);
                }
                return r;
            }));

            if (hasUpdates) {
                setInternalRequests(updatedRequests);
                // Also persist to server if opportunity is not a draft
                if (opportunity.status !== 'draft') {
                    try {
                        const { data: _opportunity } = await axios.put(
                            `/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`,
                            { opportunity: { rateRequests: updatedRequests, buyingRates: opportunity.buyingRates || [] } }
                        );
                        onUpdateOpportunity({ opportunity: _opportunity });
                    } catch (e) {
                        console.error('[SidePaneRfqs] Error persisting updated requests', e);
                    }
                }
            }
        };

        // Check immediately
        checkPendingStatuses();

        // Poll every 3 seconds while there are pending requests
        const interval = setInterval(checkPendingStatuses, 3000);
        return () => clearInterval(interval);
    }, [internalRequests, organization, accesstoken, opportunity.status, opportunity.id, opportunity.buyingRates, onUpdateOpportunity]);

    // Poll for RFQ replies every 5s
    useEffect(() => {
        const interval = setInterval(() => setRefreshTick(t => t + 1), 5000);
        return () => clearInterval(interval);
    }, [gettingInfo]);

    const source = selectedRoute?.source;
    const destination = selectedRoute?.destination;

    const internalRequestsRef = useRef(internalRequests);
    internalRequestsRef.current = internalRequests;
    const conversationCacheRef = useRef({}); // { [conversationId]: { messageCount, info, subject, body } }
    // Conversation ids the user explicitly asked to re-parse (one-shot). The poll consumes
    // these to bypass the idempotency lock (server) + in-memory skip and force a fresh AI parse.
    const forceReparseRef = useRef(new Set());

    function triggerReparse(conversationId) {
        if (!conversationId) return;
        forceReparseRef.current.add(conversationId);
        delete conversationCacheRef.current[conversationId]; // clear in-memory skip so the poll re-POSTs
        setGettingInfo(true);
        setRefreshTick(t => t + 1);
    }

    // Guarded entry point from the "Re-parse" button. Warns before regenerating costs that
    // the user has manually edited (overridden) or that are already used in an offer.
    // Returns true if a re-parse was triggered, false if the user cancelled.
    function handleReparseClick(e, conversationId, threadRequests) {
        if (e) e.stopPropagation();
        const threadRateIds = new Set(_.compact((threadRequests || []).flatMap(tr => (tr.rates || []).map(rt => rt.id))));
        const hasOverridden = (buyingRates || []).some(br => threadRateIds.has(br.id) && br.overridden);
        const usedInOffer = (buyingRates || []).some(br => threadRateIds.has(br.id));
        if (hasOverridden || usedInOffer) {
            const msg = hasOverridden
                ? 'Re-parse this reply with AI? Costs you manually edited are kept as-is; the AI-generated ones will be regenerated (you may briefly see both).'
                : 'Some costs from this reply are used in an offer. Re-parsing regenerates them and may change the amounts. Continue?';
            if (!window.confirm(msg)) return false;
        }
        triggerReparse(conversationId);
        return true;
    }

    useEffect(() => {
        if (!emailLoginInfo || emailLoginInfo.status !== 'connected') {
            setGettingInfo(false);
            setConversations({});
            return;
        }
        (async () => {
            try {
                const currentRequests = internalRequestsRef.current;
                let _conversations = {};
                // Deduplicate by thread — only fetch each conversation once
                const requestsWithConvo = currentRequests.filter(r => (!!r.attributes.conversationId || !!r.attributes.internetMessageId) && isRequestAccessible(r));
                const uniqueThreadRequests = _.uniqBy(requestsWithConvo, r => r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId || r.id);
                const result = await Promise.all(uniqueThreadRequests.map(async r => {
                    const conversationId = r.attributes.conversationId;
                    const internetMessageId = r.attributes.internetMessageId;

                    let conversations = [];
                    let moreConversations = [];
                    try {
                        conversations = conversationId ? await email.getMessagesForConversation({ org: organization, conversationId, tokens: emailLoginInfo.tokens }) : [];
                    } catch (e) {
                        console.error('[OpportunityRateRequestPane] Error getting info for request', r, e);
                    }
                    try {
                        moreConversations = internetMessageId ? await email.getMessagesForConversation({ org: organization, internetMessageId, tokens: emailLoginInfo.tokens }) : [];
                    } catch (e) {
                        console.error('[OpportunityRateRequestPane] Error getting info for request', r, e);
                    }

                    const all = _.sortBy(
                        _.uniqBy([...(conversations || []), ...(moreConversations || [])], a => `${a.id}-${a.origin?.id}`),
                        (msg) => new Date(msg.receivedDateTime).getTime()
                    );
                    const latest = _.last(all);

                    if (!latest) return { request: r };

                    _conversations[conversationId] = all;

                    // Consume any one-shot force-reparse request for this thread (set by the
                    // "Re-parse" button). When forced we bypass the in-memory skip and tell the
                    // server to bypass its idempotency lock (reparse=true) for a fresh AI parse.
                    const forced = forceReparseRef.current.has(conversationId);
                    if (forced) forceReparseRef.current.delete(conversationId);

                    // Skip server call if message count hasn't changed since last poll
                    const cached = conversationCacheRef.current[conversationId];
                    if (!forced && cached && cached.messageCount === all.length) {
                        return { request: r, info: cached.info, subject: cached.subject, body: cached.body };
                    }

                    // Download non-inline attachments from all messages
                    const attachments = [];
                    for (const msg of all) {
                        if (msg.attachments && msg.attachments.length > 0) {
                            for (const att of msg.attachments) {
                                if (!att.isInline) {
                                    try {
                                        const url = await email.getAttachmentUrl({ org: organization, tokens: emailLoginInfo.tokens, messageId: msg.id, attachment: att, monadaAccesstoken: accesstoken });
                                        attachments.push({ url, name: att.name, contentType: att.contentType });
                                    } catch (e) {
                                        console.error('[SidePaneRfqs] Error downloading attachment', att.name, e);
                                    }
                                }
                            }
                        }
                    }

                    const { data: info } = await axios.post(`/api/emailconversations/${conversationId}?organizationId=${organization.id}&accesstoken=${accesstoken}${forced ? '&reparse=true' : ''}`, {
                        subject: latest.subject,
                        body: latest.body.content,
                        attachments
                    });

                    // Cache the result
                    conversationCacheRef.current[conversationId] = { messageCount: all.length, info, subject: latest.subject, body: latest.body.content };

                    const replyAttachments = all.flatMap(m => (m.attachments || []).filter(a => !a.isInline).map(a => ({ name: a.name, size: a.size })));
                    return { request: r, info, subject: latest.subject, body: latest.body.content, attachmentNames: replyAttachments };
                }));

                setConversations(_conversations);

                // Convert offers to rates per (thread, product) pair. apiOfferToRates filters
                // by request.product.type, so a single conversation that covers multiple products
                // (e.g. supplier replies with both 20' and 40' rates) needs one cache entry per
                // product. result[] only contains the first rateRequest per thread, so iterate
                // currentRequests to discover every (thread, product) pair and reuse the thread's
                // already-fetched info.offer for each. Same-(thread, product) rateRequests share
                // the cached rates so UI deduplication still works.
                const threadInfoByKey = {};
                for (const res of result) {
                    if (!res.info?.offer) continue;
                    const threadKey = res.request.attributes?.conversationId || res.request.attributes?.internetMessageId || res.request.attributes?.pendingId || res.request.id;
                    threadInfoByKey[threadKey] = res;
                }
                const threadRatesCache = {};
                for (const r of currentRequests) {
                    const threadKey = r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId || r.id;
                    const res = threadInfoByKey[threadKey];
                    if (!res) continue;
                    const cacheKey = `${threadKey}|${r.product?.id || r.product?.type || ''}`;
                    if (!threadRatesCache[cacheKey]) {
                        const apiRates = await apiOfferToRates({ offer: res.info.offer, request: r, unlocode, seaPorts });
                        apiRates.forEach(rate => {
                            rate.platform = 'monada-ai-magic';
                            rate.created = new Date().getTime();
                            rate.attributes = {
                                sourceContent: `Subject: ${res.subject}\n\n${res.body}`,
                                ...(res.attachmentNames?.length > 0 ? { attachments: res.attachmentNames } : {}),
                            };
                        });
                        threadRatesCache[cacheKey] = apiRates;
                    }
                }

                // Fingerprint a rate by its semantic content (supplier + product + charges)
                // so we can match rates across API calls that generate new UUIDs
                function rateFingerprint(rate) {
                    const charges = (rate.offer?.sections || [])
                        .flatMap(s => (s.offers || []).flatMap(o => (o.fields || []).map(f => `${f.title}|${f.type}|${JSON.stringify(f.values)}`)))
                        .sort().join(';');
                    return `${rate.supplier?.organization}|${rate.product?.type}|${charges}`;
                }

                // Iterate over result, and create a new requests list
                const newRequests = currentRequests.map(r => {
                    const threadKey = r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId || r.id;
                    const cacheKey = `${threadKey}|${r.product?.id || r.product?.type || ''}`;
                    const apiRates = threadRatesCache[cacheKey];
                    if (!apiRates) return r;

                    // Build fingerprint map from existing rates to preserve their IDs
                    const existingByFingerprint = {};
                    for (const rate of (r.rates || [])) {
                        existingByFingerprint[rateFingerprint(rate)] = rate;
                    }

                    // Check if rates have actually changed by comparing fingerprints
                    const apiFingerprints = apiRates.map(rateFingerprint);
                    const existingFingerprints = (r.rates || []).map(rateFingerprint);
                    if (apiFingerprints.length === existingFingerprints.length &&
                        apiFingerprints.every(fp => existingFingerprints.includes(fp))) {
                        return r; // Same rates, no update needed
                    }

                    // Carry over existing IDs for matching rates to keep buyingRates references stable
                    const mergedRates = apiRates.map(apiRate => {
                        const fp = rateFingerprint(apiRate);
                        const existing = existingByFingerprint[fp];
                        if (existing) {
                            delete existingByFingerprint[fp]; // consume so duplicates get new IDs
                            return { ...apiRate, id: existing.id };
                        }
                        return apiRate;
                    });

                    return { ...r, rates: mergedRates };
                });

                if (!_.isEqual(newRequests, currentRequests)) {
                    // Auto-commit: store conversation snapshots so other users can see them
                    const committedRequests = newRequests.map(r => {
                        const convId = r.attributes?.conversationId;
                        const conv = _conversations[convId];
                        if (!conv && !r.rates?.length) return r;
                        return {
                            ...r,
                            attributes: {
                                ...r.attributes,
                                ...(conv ? { conversationSnapshot: conv.map(m => ({ id: m.id, subject: m.subject, body: m.body?.content, from: m.from, receivedDateTime: m.receivedDateTime, hasAttachments: m.hasAttachments, attachments: (m.attachments || []).map(a => ({ id: a.id, name: a.name, size: a.size, isInline: a.isInline, contentType: a.contentType })) })) } : {}),
                                lastSyncedBy: connectedEmail,
                                lastSyncedAt: Date.now(),
                            }
                        };
                    });
                    setInternalRequests(committedRequests);
                    // Persist to server in the background
                    if (opportunity.status !== 'draft') {
                        axios.put(
                            `/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`,
                            { opportunity: { rateRequests: committedRequests, buyingRates: opportunity.buyingRates || [] } }
                        ).then(({ data }) => onUpdateOpportunity({ opportunity: data }))
                         .catch(e => console.error('[SidePaneRfqs] Error auto-committing', e));
                    }
                }
                setGettingInfo(false);
            } catch (e) {
                console.error('[OpportunityRateRequestPane] Error getting info', e);
            }
        })();
    }, [emailLoginInfo?.status, refreshTick]);

    const cleanBuyingRates = useMemo(() => {
        return buyingRates.filter(r => !internalRequests.find(ir => ir.rates?.find(rate => rate.id === r.id)));
    }, [buyingRates, internalRequests]);

    // Determine which RFQ threads the connected mailbox can access (fetch email conversations for)
    const connectedEmail = emailLoginInfo?.email;
    const inaccessibleCount = useMemo(() => {
        if (!connectedEmail || emailLoginInfo?.status !== 'connected') return 0;
        const grouped = _.groupBy(internalRequests, r => r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId || r.id);
        let count = 0;
        for (const [, threadRequests] of Object.entries(grouped)) {
            const sentBy = threadRequests[0]?.attributes?.sentBy;
            if (sentBy && sentBy !== connectedEmail) count++;
        }
        return count;
    }, [internalRequests, connectedEmail, emailLoginInfo?.status]);

    /** Returns true if the connected mailbox can access this request's email thread */
    function isRequestAccessible(r) {
        if (!connectedEmail || emailLoginInfo?.status !== 'connected') return true;
        const sentBy = r.attributes?.sentBy;
        return !sentBy || sentBy === connectedEmail;
    }

    // While a reply drawer is open, keep its parsed rates in sync with the latest poll results
    // (e.g. after a "Re-parse with AI") so the update appears in place instead of navigating away.
    useEffect(() => {
        if (!rateMoreInfo.open || !rateMoreInfo.conversationId) return;
        const threadRates = _.uniqBy(_.compact(
            internalRequests
                .filter(ir => ir.attributes?.conversationId === rateMoreInfo.conversationId)
                .flatMap(ir => ir.rates)
        ), rate => rate.id);
        if (threadRates.length === 0) return;
        const sig = rs => rs.map(r => `${r.id}:${r.modified || r.created || ''}`).join('|');
        if (sig(threadRates) !== sig(rateMoreInfo.rates || [])) {
            setRateMoreInfo(prev => ({ ...prev, rates: threadRates }));
            if (reparseAwaitingRef.current === rateMoreInfo.conversationId) {
                reparseAwaitingRef.current = null;
                setReparseNotice({ severity: 'success', message: opportunity.status !== 'draft' ? 'Re-parsed and saved.' : 'Re-parsed.' });
            }
        }
    }, [internalRequests, rateMoreInfo.open, rateMoreInfo.conversationId]); // eslint-disable-line react-hooks/exhaustive-deps


    const productsStr = useMemo(() => {
        const description = (products || []).map(p => getProductDescription({ product: p, transportationMethod, allProducts: products, organization: organization })).join('\n');
        // React Render ProductSummary to string
        const summary = ReactDOM.renderToStaticMarkup(<ProductsSummary noBorder={true} products={products} transportationMethod={transportationMethod} organization={organization} />);
        return `${description}\n${summary}`;
    }, [products]);

    function onAddRequest() {
        setSelectedSuppliers([]);
        setSelectedRoute(null);
        setScreen('email-picker');
    }

    async function onSendRequest(sent) {
        setScreen(null);
        // Count every distinct address the RFQ actually went to — including CC recipients
        // (ccRecipients repeats across grouped sends, so dedupe by address).
        const addr = r => (r?.address || r?.email || r || '').toString().trim().toLowerCase();
        const recipients = new Set();
        sent.forEach(s => {
            (s.toRecipients || []).forEach(r => recipients.add(addr(r)));
            (s.ccRecipients || []).forEach(r => recipients.add(addr(r)));
        });
        const recipientCount = [...recipients].filter(Boolean).length;
        setSentToast(recipientCount > 0
            ? `RFQ sent to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}.`
            : 'RFQ sent successfully.');
        setRequests([
            ...requests,
            ..._.compact(sent.map(s => {
                // Support multiple supplier contacts (all toRecipients).
                // For each toRecipient, try to find a supplier/contact, else fallback.
                return s.toRecipients.flatMap(toRecipient => {
                    let supplier = selectedSuppliers.find(supplier => 
                        Array.isArray(supplier.contacts)
                            ? supplier.contacts.some(contact => contact.email === toRecipient.address)
                            : supplier.email === toRecipient.address
                    );

                    if (!supplier) {
                        supplier = {
                            name: toRecipient.name || toRecipient.address,
                            email: toRecipient.address,
                            uniqueId: 'unknown',
                            contacts: []
                        };
                    }
                    
                    // Only build rate-requests for products valid in the current transport mode.
                    // After a mode switch a stale product can linger (e.g. a "20' Dry" container on
                    // an air opportunity); minimizing it under the wrong mode strips required fields
                    // (quantity/commodity) and the opportunity PUT then fails schema validation (400).
                    const validProducts = (products || []).filter(p => (PRODUCT_TYPES[transportationMethod] || []).some(pt => pt.name === p.type));
                    return validProducts.map(product => ({
                        id: uuidv4(),
                        type: 'rate-request',
                        created: Date.now(),
                        modified: Date.now(),
                        transportationMethod,
                        source,
                        destination,
                        supplier: {
                            ..._.pick(supplier, ['uniqueId']),
                            organization: supplier.name,
                            email: toRecipient.address,  // use actual toRecipient
                        },
                        product: minimizeProducts({ products: [product], transportationMethod })[0],
                        attributes: {
                            ..._.pick(s, ['status', 'pendingId', 'createdAt', 'conversationId', 'internetMessageId']),
                            sentBy: emailLoginInfo?.email,
                        }
                    }));
                });
            }).flat())
        ]);
    }

    async function setRequests(a) {
        if (opportunity.status === 'draft') {
            onUpdateOpportunity({ opportunity: { ...opportunity, rateRequests: a } });
            return true;
        }

        setError(false);
        try {
            // Preserve existing buyingRates when updating rateRequests
            const { data: _opportunity } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, { 
                opportunity: { 
                    rateRequests: a,
                    // Explicitly include buyingRates to ensure they are preserved
                    buyingRates: opportunity.buyingRates || []
                } 
            });
            onUpdateOpportunity({ opportunity: _opportunity });
            return true;
        } catch(e) {
            console.error('[SidePaneRfqs] Error setting requests', e);
            setError(true);
            return false;
        }
    }

    // Sent/parsed timestamp (ms) for an RFQ row or AI-parsed rate
    function rowSentAt(r) {
        const sentAt = r?.attributes?.createdAt || r?.created;
        return sentAt ? new Date(sentAt).getTime() : 0;
    }

    // A row is "carried over" if it predates this opportunity — i.e. it came in with a
    // duplicate. Duplication (OpportunityRouter.onDuplicate) copies rateRequests verbatim
    // but resets `created`, so anything sent before opportunity.created was inherited here,
    // not sent from this opportunity. These are collapsed to keep the queue focused on new work.
    const oppCreatedMs = opportunity?.created ? new Date(opportunity.created).getTime() : 0;
    function isCarriedOver(r) {
        const t = rowSentAt(r);
        return !!oppCreatedMs && !!t && t < oppCreatedMs;
    }

    // Re-parse a free-text ("Copy from Anywhere") rate by re-running the AI parser on the
    // original pasted text stored in attributes.sourceContent. Keeps the same rate id so
    // offer links are preserved. Mirrors SidePane.clipboarddialog.onDoIt.
    async function reparseFreeText(rate) {
        const sourceContent = rate?.attributes?.sourceContent;
        if (!sourceContent) return;
        if (!window.confirm('Re-parse this free-text cost with AI? The current charges will be regenerated from the original text.')) return;
        setReparsingFreeTextId(rate.id);
        try {
            const { data: response } = await axios.post(`/api/emailconversations/ai-get-rates?accesstoken=${accesstoken}&organizationId=${organization.id}`, {
                body: sourceContent,
                subject: `Supplier Rates for product ${(products || []).map(p => p.type).join(', ')}`,
            });
            const product = rate.product || (products || [])[0];
            const parsed = _.compact((await Promise.all((product ? [product] : []).map(p => apiOfferToRates({
                offer: response,
                request: { product: p, source: rate.source, destination: rate.destination, transportationMethod, supplier: rate.supplier },
                unlocode,
                seaPorts,
            })))).flat());
            if (parsed.length === 0) {
                alert('Could not parse any rates from the saved content.');
                return;
            }
            const now = Date.now();
            const replacement = { ...parsed[0], id: rate.id, attributes: { sourceContent }, platform: 'monada-ai-magic', created: rate.created || now, modified: now };
            const updatedBuyingRates = (opportunity.buyingRates || []).map(br => br.id === rate.id ? replacement : br);
            if (opportunity.status !== 'draft') {
                const { data: _opp } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, { opportunity: { rateRequests: internalRequests, buyingRates: updatedBuyingRates } });
                onUpdateOpportunity({ opportunity: _opp });
            } else {
                onUpdateOpportunity({ opportunity: { ...opportunity, buyingRates: updatedBuyingRates } });
            }
            // Stay on the drawer and show the freshly parsed rate in place
            setRateMoreInfo(prev => ({ ...prev, rates: [replacement] }));
            setReparseNotice({ severity: 'success', message: opportunity.status !== 'draft' ? 'Re-parsed and saved.' : 'Re-parsed.' });
        } catch (e) {
            console.error('[RFQ] free-text re-parse error', e);
            alert('Failed to re-parse. Please try again.');
        } finally {
            setReparsingFreeTextId(null);
        }
    }

    // Re-parse triggered from next to a rate's "View source content" link in the drawer.
    // An email-reply rate re-parses its whole thread (via conversationId); a free-text rate
    // re-parses its saved pasted text.
    function onReparseSource(rate) {
        if (rateMoreInfo.conversationId) {
            const treq = internalRequests.filter(ir => ir.attributes?.conversationId === rateMoreInfo.conversationId);
            if (handleReparseClick(null, rateMoreInfo.conversationId, treq)) {
                // Stay on the drawer; the async poll refreshes the rates below (see effect) and saves.
                reparseAwaitingRef.current = rateMoreInfo.conversationId;
                setReparseNotice({ severity: 'info', message: 'Re-parsing with AI — the rates below will refresh automatically.' });
            }
        } else {
            reparseFreeText(rate);
        }
    }

    // Renders a single RFQ/rate row inside a supplier card, with a timeline rail dot.
    function renderRequestRow(r, index, rows, br) {
        const sentAt = r.attributes?.createdAt || r.created;
        let sentLabel = 'Free Text';
        if (r.platform !== 'monada-ai-magic' && sentAt) {
            const sentDate = new Date(sentAt);
            const today = new Date();
            const isToday = sentDate.getDate() === today.getDate() && sentDate.getMonth() === today.getMonth() && sentDate.getFullYear() === today.getFullYear();
            sentLabel = isToday
                ? `Request from ${sentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                : `Request from ${sentDate.toLocaleDateString([], { day: 'numeric', month: 'short' })}`;
        }

        const threadKey = r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId;
        const threadRequests = br.filter(tr => (tr.attributes?.conversationId || tr.attributes?.internetMessageId || tr.attributes?.pendingId) === threadKey);
        const threadHasRates = threadRequests.some(tr => tr.rates);
        const conversation = conversations[r.attributes?.conversationId];
        const onlySent = conversation && conversation.length === 1;
        const hasConversationId = !!(r.attributes?.conversationId || r.attributes?.internetMessageId);

        // Determine status
        const accessible = isRequestAccessible(r);
        let statusText = null;
        let statusColor = null;
        let isLoading = false;
        if (r.platform === 'monada-ai-magic') {
            statusText = 'Processed';
            statusColor = { bg: '#dcfce7', text: '#166534' };
        } else if (!accessible) {
            statusText = `Sent by ${r.attributes?.sentBy}`;
            statusColor = { bg: '#f1f5f9', text: '#64748b' };
        } else if (r.attributes?.status === 'error') {
            statusText = 'Error';
            statusColor = { bg: '#fee2e2', text: '#991b1b' };
        } else if (r.attributes?.status === 'pending') {
            statusText = 'Sending...';
            statusColor = { bg: '#fef9c3', text: '#92400e' };
            isLoading = true;
        } else if (conversation && threadHasRates && !gettingInfo && emailLoginInfo?.status === 'connected') {
            statusText = 'Replied';
            statusColor = { bg: '#dcfce7', text: '#166534' };
        } else if (conversation && !threadHasRates && !onlySent && !gettingInfo && emailLoginInfo?.status === 'connected') {
            statusText = 'Replied (no rates)';
            statusColor = { bg: '#fef3c7', text: '#92400e' };
        } else if (conversation && !threadHasRates && onlySent && !gettingInfo && emailLoginInfo?.status === 'connected') {
            statusText = 'Sent';
            statusColor = { bg: '#dbeafe', text: '#1e40af' };
        } else if (hasConversationId && !conversation && emailLoginInfo?.status === 'connected') {
            isLoading = accessible;
        } else if (accessible && r.platform !== 'monada-ai-magic' && (gettingInfo || !conversation) && !hasConversationId) {
            isLoading = true;
        }

        const dotBg = statusColor?.bg || '#e2e8f0';
        const dotBorder = statusColor?.text || '#cbd5e1';

        return (
            <Box key={r.id} sx={{ display: 'flex', alignItems: 'stretch' }}>
                {/* Timeline rail: continuous line + status dot */}
                <Box sx={{ position: 'relative', width: '24px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ width: '2px', backgroundColor: '#eef2f6' }} />
                    <Box sx={{ position: 'absolute', top: '22px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: dotBg, border: `2px solid ${dotBorder}`, boxShadow: '0 0 0 3px #ffffff' }} />
                </Box>
                {/* Row content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        onClick={() => {
                            if (r.platform === 'monada-ai-magic') {
                                setRateMoreInfo({
                                    open: true,
                                    rates: [r],
                                });
                            } else {
                                const tk = r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId;
                                const treq = br.filter(tr => (tr.attributes?.conversationId || tr.attributes?.internetMessageId || tr.attributes?.pendingId) === tk);
                                const allRates = _.uniqBy(_.compact(treq.flatMap(tr => tr.rates)), rate => rate.id);
                                setRateMoreInfo({
                                    open: true,
                                    rates: allRates.length > 0 ? allRates : r.rates,
                                    conversationId: r.attributes.conversationId,
                                    conversationSnapshot: r.attributes?.conversationSnapshot,
                                    lastSyncedAt: r.attributes?.lastSyncedAt,
                                    lastSyncedBy: r.attributes?.lastSyncedBy,
                                });
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px 0px',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease-in-out',
                            '&:hover': {
                                backgroundColor: 'rgba(248, 250, 252, 0.5)',
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <Typography
                                variant='body2'
                                sx={{
                                    fontSize: '13px',
                                    color: '#0f172a',
                                    fontWeight: 600,
                                    letterSpacing: '-0.01em',
                                    fontFamily: 'Poppins, sans-serif'
                                }}
                            >
                                {sentLabel}
                            </Typography>
                            {r.platform === 'monada-ai-magic' && (r.created || r.modified) && (
                                <Typography
                                    variant='caption'
                                    sx={{
                                        fontSize: '11px',
                                        color: '#94a3b8',
                                        fontFamily: 'Poppins, sans-serif',
                                        mt: '2px'
                                    }}
                                >
                                    Parsed {new Date(r.created || r.modified).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}
                                </Typography>
                            )}
                            {r.platform !== 'monada-ai-magic' && (() => {
                                const tk = r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId;
                                const threadReqs = br.filter(tr => (tr.attributes?.conversationId || tr.attributes?.internetMessageId || tr.attributes?.pendingId) === tk);
                                const recipients = _.uniq(threadReqs.map(tr => tr.supplier?.email).filter(Boolean));
                                return recipients.length > 0 ? (
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            fontSize: '12px',
                                            color: '#64748b',
                                            letterSpacing: '-0.01em',
                                            fontFamily: 'Poppins, sans-serif',
                                            mt: '2px'
                                        }}
                                    >
                                        {recipients.join(', ')}
                                    </Typography>
                                ) : null;
                            })()}
                        </Box>
                        {statusText && statusColor && (
                            <Box
                                sx={{
                                    backgroundColor: statusColor.bg,
                                    color: statusColor.text,
                                    padding: '4px 12px',
                                    borderRadius: '9999px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    fontFamily: 'Poppins, sans-serif'
                                }}
                            >
                                {statusText}
                            </Box>
                        )}
                        {isLoading && (
                            <CircularProgress size={14} sx={{ color: '#64748b', marginLeft: statusText ? '6px' : '' }} />
                        )}
                    </Box>
                    {!accessible && r.attributes?.lastSyncedAt && (
                        <Typography variant='caption' sx={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic', fontFamily: 'Poppins, sans-serif', mt: 0.5 }}>
                            Last synced {new Date(r.attributes.lastSyncedAt).toLocaleDateString([], { day: 'numeric', month: 'short' })} by {r.attributes.lastSyncedBy} — may not be up to date
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    if (rateMoreInfo.open) {
        return (
            <SidePaneRfqsDrawer
                onClose={() => { setReparseNotice(null); reparseAwaitingRef.current = null; setRateMoreInfo({ ...rateMoreInfo, open: false }); }}
                rates={rateMoreInfo.rates ? rateMoreInfo.rates : []}
                org={organization}
                exchangeRates={exchangeRates}
                setMinimizedSearch={setMinimizedSearch}
                products={products}
                onReparseSource={onReparseSource}
                reparsingSourceId={reparsingFreeTextId}
                epilogue={(
                    <Box>
                        {reparseNotice && (
                            <Alert severity={reparseNotice.severity} onClose={() => setReparseNotice(null)} sx={{ mb: 2 }}>
                                {reparseNotice.message}
                            </Alert>
                        )}
                        {rateMoreInfo.conversationId && rateMoreInfo.rates && rateMoreInfo.rates[0]?.platform === 'monada-ai-magic' && (
                            <Box sx={{ width: '100%', background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', boxShadow: 'none' }}>
                                <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                                    Source Email Correspondence
                                </Typography>
                            </Box>
                        )}
                        {rateMoreInfo?.rates?.[0]?.platform !== 'monada-ai-magic' && !conversations[rateMoreInfo?.conversationId] && emailLoginInfo?.status !== 'connected' && (
                            <Alert severity='info' sx={{ mb: 1 }}>
                                <AlertTitle>Connect Your Email to View the Conversation</AlertTitle>
                                To see the full conversation right here in Monada, connect the email account you used to send the rate request.
                                <br/>
                                <br/>
                                <email.LoginButton
                                    org={organization}
                                    accesstoken={accesstoken}
                                    onDone={onEmailRefreshConnection}
                                />
                            </Alert>
                        )}
                        {rateMoreInfo?.rates?.[0]?.platform !== 'monada-ai-magic' && !conversations[rateMoreInfo?.conversationId] && !rateMoreInfo?.conversationSnapshot && emailLoginInfo?.status === 'connected' && (
                            <Alert severity='info' sx={{ mb: 1 }}>
                                <AlertTitle>Conversation not found</AlertTitle>
                                No conversation found for this rate request. Please check with the email account you used to send the rate request.
                            </Alert>
                        )}
                        {conversations[rateMoreInfo?.conversationId] && (
                            <EmailConversation
                                padding={0}
                                width={'100%'}
                                conversation={conversations[rateMoreInfo?.conversationId]}
                                organization={organization}
                                tokens={emailLoginInfo.tokens}
                                accesstoken={accesstoken}
                                emailLoginInfo={emailLoginInfo}
                                noControls={true}
                            />
                        )}
                        {!conversations[rateMoreInfo?.conversationId] && rateMoreInfo?.conversationSnapshot && (
                            <Box>
                                <Alert severity='warning' sx={{ mb: 1, fontSize: '12px' }} action={
                                    rateMoreInfo?.conversationId ? (
                                    emailLoginInfo?.status === 'connected' ? (
                                        <Button size='small' color='inherit' sx={{ fontSize: '11px', textTransform: 'none', whiteSpace: 'nowrap' }} onClick={async () => {
                                            try {
                                                const conv = await email.getMessagesForConversation({ org: organization, tokens: emailLoginInfo.tokens, conversationId: rateMoreInfo.conversationId });
                                                if (!conv || conv.length === 0) return;
                                                const snapshot = conv.map(m => ({ id: m.id, subject: m.subject, body: m.body?.content, from: m.from, receivedDateTime: m.receivedDateTime, hasAttachments: m.hasAttachments, attachments: (m.attachments || []).map(a => ({ id: a.id, name: a.name, size: a.size, isInline: a.isInline, contentType: a.contentType })) }));
                                                const updatedRequests = internalRequests.map(r => r.attributes?.conversationId === rateMoreInfo.conversationId ? { ...r, attributes: { ...r.attributes, conversationSnapshot: snapshot, lastSyncedBy: emailLoginInfo.email, lastSyncedAt: Date.now() } } : r);
                                                setInternalRequests(updatedRequests);
                                                if (opportunity.status !== 'draft') {
                                                    const { data: _opp } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${organization.id}`, { opportunity: { rateRequests: updatedRequests, buyingRates: opportunity.buyingRates || [] } });
                                                    onUpdateOpportunity({ opportunity: _opp });
                                                }
                                            } catch (e) { console.error('[RFQ] Re-sync error', e); }
                                        }}>Re-sync</Button>
                                    ) : (
                                        <Tooltip title='Connect your email to re-sync this conversation' arrow placement='top'>
                                            <span><Button size='small' color='inherit' disabled sx={{ fontSize: '11px', textTransform: 'none', whiteSpace: 'nowrap' }}>Re-sync</Button></span>
                                        </Tooltip>
                                    )) : null
                                }>
                                    Showing saved conversation snapshot{rateMoreInfo.lastSyncedBy ? ` from ${rateMoreInfo.lastSyncedBy}` : ''}{rateMoreInfo.lastSyncedAt ? ` (${new Date(rateMoreInfo.lastSyncedAt).toLocaleDateString([], { day: 'numeric', month: 'short' })})` : ''}. This may not reflect the latest emails.
                                </Alert>
                                {rateMoreInfo.conversationSnapshot.map((msg, i) => (
                                    <Box key={i} sx={{ mb: 2, p: 2, border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                                                {msg.from?.emailAddress?.name || msg.from?.emailAddress?.address || 'Unknown'}
                                            </Typography>
                                            {msg.receivedDateTime && (
                                                <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
                                                    {new Date(msg.receivedDateTime).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                </Typography>
                                            )}
                                        </Box>
                                        {msg.subject && (
                                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#475569', mb: 0.5 }}>
                                                {msg.subject}
                                            </Typography>
                                        )}
                                        <Box sx={{ fontSize: '12px', color: '#64748b', maxHeight: '200px', overflowY: 'auto' }}
                                            dangerouslySetInnerHTML={{ __html: msg.body || '' }}
                                        />
                                        {(msg.attachments || []).filter(a => !a.isInline).length > 0 && (
                                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {(msg.attachments || []).filter(a => !a.isInline).map((att, j) => (
                                                    <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', '&:hover': { backgroundColor: '#f1f5f9' } }}
                                                        onClick={async () => {
                                                            try {
                                                                const url = await email.getAttachmentUrl({ org: organization, monadaAccesstoken: accesstoken, tokens: emailLoginInfo?.tokens, messageId: msg.id, attachment: att });
                                                                window.open(url, '_blank');
                                                            } catch (e) {
                                                                console.error('[Snapshot] Error downloading attachment', e);
                                                                alert(e.message?.includes('not connected') ? 'Please connect your email to download attachments.' : 'Failed to download attachment. The file may not be accessible from your connected mailbox.');
                                                            }
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: '11px', color: '#4F46E5', fontWeight: 500 }}>{att.name || 'Attachment'}</Typography>
                                                        {att.size && <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>({(att.size / 1024).toFixed(0)} KB)</Typography>}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {conversations[rateMoreInfo?.conversationId] && !(rateMoreInfo.rates && rateMoreInfo.rates[0]?.platform === 'monada-ai-magic') && (
                            <Alert severity='info'>
                                RFQ sent successfully! Waiting for supplier reply containing rates.
                            </Alert>
                        )}
                    </Box>
                )}
            />
        );  
    }


    return (
        <Box sx={{ width: '100%', background: theme.palette.background.default, pb: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: '100%', background: 'transparent', padding: '6px 24px', mb: 3, borderBottom: '1px solid rgba(59, 130, 246, 0.3)', boxShadow: 'none' }}>
                <Typography variant='body1' sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#334155' }}>
                    RFQs to Suppliers
                </Typography>
            </Box>
            {/* Opportunities autosave, so "not yet saved" is misleading once autosave has run.
                The real prerequisite for RFQs is having products — show that single message. */}
            {_.isEmpty(products) && (
                <Alert severity='warning' fullWidth variant='standard' sx={{ width: 'calc(100% - 48px)', mb: 4 }}>
                    Please add products to the opportunity first.
                </Alert>
            )}
            {!_.isEmpty(products) && opportunity.status !== 'draft' && _.isEmpty(internalRequests) && ((buyingRates || []).filter(r => r.platform === 'monada-ai-magic').length === 0) && (
                <Box sx={{ width: 'calc(100% - 48px)', mb: 4, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', px: 2 }}>
                    {/* Primary Action - Request rates via email */}
                    <Box
                        onClick={() => {
                            if (!(_.isEmpty(products) || emailLoginInfo?.status !== 'connected' || opportunity.status === 'draft' || opportunity.owner !== me.email)) {
                                onAddRequest();
                            }
                        }}
                        sx={{
                            width: '100%',
                            maxWidth: '600px',
                            padding: '32px',
                            borderRadius: '12px',
                            border: '1px solid #f1f5f9',
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                            cursor: (_.isEmpty(products) || emailLoginInfo?.status !== 'connected' || opportunity.status === 'draft' || opportunity.owner !== me.email) ? 'not-allowed' : 'pointer',
                            opacity: (_.isEmpty(products) || emailLoginInfo?.status !== 'connected' || opportunity.status === 'draft' || opportunity.owner !== me.email) ? 0.6 : 1,
                            transition: 'all 0.2s ease-in-out',
                            fontFamily: 'Poppins, sans-serif',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                border: '1px solid #e2e8f0',
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box sx={{ 
                                width: '48px', 
                                height: '48px', 
                                borderRadius: '12px', 
                                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#ffffff', fontSize: '20px', fontWeight: 300 }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography 
                                    variant='h6' 
                                    sx={{ 
                                        fontSize: '16px', 
                                        fontWeight: 600,
                                        color: '#0f172a',
                                        mb: 0.5,
                                        fontFamily: 'Poppins, sans-serif'
                                    }}
                                >
                                    Request rates via email
                                </Typography>
                                <Typography 
                                    variant='body2' 
                                    sx={{ 
                                        fontSize: '13px', 
                                        color: '#64748b',
                                        fontFamily: 'Poppins, sans-serif'
                                    }}
                                >
                                    Automate outreach to your supplier network.
                                </Typography>
                                {emailLoginInfo?.status !== 'connected' && (
                                    <Typography 
                                        variant='caption' 
                                        sx={{ 
                                            fontSize: '11px', 
                                            color: '#ef4444',
                                            mt: 0.5,
                                            display: 'block'
                                        }}
                                    >
                                        (Not connected to email)
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                </Box>
            )}
            {error && (
                <Alert severity='error' fullWidth variant='standard' icon={false} sx={{ width: 'calc(100% - 48px)', mb: 4 }}>
                    Error saving requests. Please try again.
                </Alert>
            )}
            {!_.isEmpty(products) && opportunity.status !== 'draft' && (!_.isEmpty(internalRequests) || ((buyingRates || []).filter(r => r.platform === 'monada-ai-magic').length > 0)) && (
                <Box sx={{ margin: '0px 24px 24px 24px', width: 'calc(100% - 48px)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {_([...(internalRequests || []), ...((cleanBuyingRates || []).filter(r => r.platform === 'monada-ai-magic'))])
                            .groupBy(a => a.supplier.organization)
                            .values()
                            .map(br => {
                                const rowKey = x => x.attributes?.conversationId || x.attributes?.internetMessageId || x.attributes?.pendingId || x.id;
                                const uniqueRows = _.uniqBy(br, rowKey).slice().sort((a, b) => rowSentAt(b) - rowSentAt(a));
                                const currentRows = uniqueRows.filter(x => !isCarriedOver(x));
                                const carriedRows = uniqueRows.filter(x => isCarriedOver(x));
                                const supplierOrg = br[0].supplier.organization;
                                // When every row is carried over there's nothing "current" to anchor the
                                // collapse under, so show them directly rather than hiding the whole card.
                                const visibleRows = currentRows.length > 0 ? currentRows : carriedRows;
                                const collapsibleRows = currentRows.length > 0 ? carriedRows : [];
                                const expanded = !!expandedCarriedOver[supplierOrg];
                                return { br, uniqueRows, visibleRows, collapsibleRows, supplierOrg, expanded, latest: uniqueRows.length ? rowSentAt(uniqueRows[0]) : 0 };
                            })
                            .orderBy(['latest'], ['desc'])
                            .map(({ br, uniqueRows, visibleRows, collapsibleRows, supplierOrg, expanded }) => {
                            return (
                            <Box 
                                key={br[0].id} 
                                sx={{ 
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #f1f5f9',
                                    borderRadius: '8px',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                    padding: '24px',
                                    fontFamily: 'Poppins, sans-serif'
                                }}
                            >
                                {/* Header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                        <Typography 
                                            variant='h6' 
                                            sx={{ 
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: '#0f172a',
                                                letterSpacing: '-0.01em',
                                                fontFamily: 'Poppins, sans-serif'
                                            }}
                                        >
                                                {br[0].supplier.organization?.toUpperCase()}
                                        </Typography>
                                        <Typography 
                                            variant='caption' 
                                            sx={{ 
                                                fontSize: '12px',
                                                color: '#64748b',
                                                padding: '2px 8px',
                                                backgroundColor: '#f8fafc',
                                                borderRadius: '4px',
                                                    fontFamily: 'Poppins, sans-serif'
                                                }}
                                            >
                                                {_.uniq(br.map(r => r.product?.type)).filter(Boolean).join(', ')}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant='caption'
                                            sx={{
                                                fontSize: '11px',
                                                color: '#64748b',
                                                padding: '4px 10px',
                                                backgroundColor: '#f8fafc',
                                                borderRadius: '12px',
                                                fontWeight: 500,
                                                fontFamily: 'Poppins, sans-serif'
                                            }}
                                        >
                                            {_.uniqBy(br, r => r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId || r.id).length} {_.uniqBy(br, r => r.attributes?.conversationId || r.attributes?.internetMessageId || r.attributes?.pendingId || r.id).length > 1 ? 'emails' : 'email'}
                                        </Typography>
                                    </Box>

                                    {/* Request Rows — timeline, newest first */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        {visibleRows.map((r, i) => renderRequestRow(r, i, visibleRows, br))}
                                        {collapsibleRows.length > 0 && (
                                            <Box>
                                                <Box
                                                    onClick={() => setExpandedCarriedOver(s => ({ ...s, [supplierOrg]: !s[supplierOrg] }))}
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '12px 0px 4px 24px', cursor: 'pointer', color: '#64748b', fontSize: '12px', fontWeight: 500, fontFamily: 'Poppins, sans-serif', '&:hover': { color: '#4F46E5' } }}
                                                >
                                                    <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} style={{ fontSize: '10px' }} />
                                                    {expanded ? 'Hide' : 'Show'} {collapsibleRows.length} earlier {collapsibleRows.length === 1 ? 'request' : 'requests'} (carried over from duplicate)
                                                </Box>
                                                {expanded && collapsibleRows.map((r, i) => renderRequestRow(r, i, collapsibleRows, br))}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        }).value()}
                    {inaccessibleCount > 0 && (
                        <Typography variant='caption' sx={{ color: '#64748b', fontSize: '12px', textAlign: 'center', padding: '8px 0', fontFamily: 'Poppins, sans-serif' }}>
                            There {inaccessibleCount === 1 ? 'is' : 'are'} {inaccessibleCount} RFQ{inaccessibleCount !== 1 ? 's' : ''} sent by others that {inaccessibleCount === 1 ? 'is' : 'are'} not accessible by your mailbox.
                        </Typography>
                    )}
                </Box>
            )}
            {!_.isEmpty(products) && opportunity.status !== 'draft' && (!_.isEmpty(internalRequests) || ((buyingRates || []).filter(r => r.platform === 'monada-ai-magic').length > 0)) && (
                <Box sx={{ width: 'calc(100% - 48px)', mb: 4, display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', px: 2 }}>
                    {/* Primary Action - Request rates via email */}
                    <Box
                        onClick={() => {
                            if (!(_.isEmpty(products) || emailLoginInfo?.status !== 'connected' || opportunity.status === 'draft' || opportunity.owner !== me.email)) {
                                onAddRequest();
                            }
                        }}
                        sx={{
                            width: '100%',
                            maxWidth: '600px',
                            padding: '32px',
                            borderRadius: '12px',
                            border: '1px solid #f1f5f9',
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                            cursor: (_.isEmpty(products) || emailLoginInfo?.status !== 'connected' || opportunity.status === 'draft' || opportunity.owner !== me.email) ? 'not-allowed' : 'pointer',
                            opacity: (_.isEmpty(products) || emailLoginInfo?.status !== 'connected' || opportunity.status === 'draft' || opportunity.owner !== me.email) ? 0.6 : 1,
                            transition: 'all 0.2s ease-in-out',
                            fontFamily: 'Poppins, sans-serif',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                border: '1px solid #e2e8f0',
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Box sx={{ 
                                width: '48px', 
                                height: '48px', 
                                borderRadius: '12px', 
                                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#ffffff', fontSize: '20px', fontWeight: 300 }} />
                        </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography 
                                    variant='h6' 
                                    sx={{ 
                                        fontSize: '16px', 
                                        fontWeight: 600,
                                        color: '#0f172a',
                                        mb: 0.5,
                                        fontFamily: 'Poppins, sans-serif'
                                    }}
                                >
                                    Request rates via email
                                </Typography>
                                <Typography 
                                    variant='body2' 
                                    sx={{ 
                                        fontSize: '13px', 
                                        color: '#64748b',
                                        fontFamily: 'Poppins, sans-serif'
                                    }}
                                >
                                    Automate outreach to your supplier network.
                                </Typography>
                    {emailLoginInfo?.status !== 'connected' && (
                                    <Typography 
                                        variant='caption' 
                                        sx={{ 
                                            fontSize: '11px', 
                                            color: '#ef4444',
                                            mt: 0.5,
                                            display: 'block'
                                        }}
                                    >
                            (Not connected to email)
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                </Box>
            )}
            {(screen === 'email-dialog' || screen === 'email-picker') && (
                <Dialog 
                    open={true} 
                    onClose={() => setScreen(null)} 
                    maxWidth='none' 
                    sx={{ zIndex: 1000001 }}
                    disableEscapeKeyDown={true}
                >
                    {screen === 'email-picker' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', padding: '12px' }}>
                            <Typography variant='h6' sx={{ flex: 1 }}>
                                Send RFQ to Suppliers
                            </Typography>
                            <IconButton color='error' onClick={() => setScreen(null)}>
                                <FontAwesomeIcon icon={faXmark} />
                            </IconButton>
                        </Box>
                    )}
                    {screen === 'email-dialog' && (
                        <Box sx={{ padding: '0px 24px' }}>
                            <SendNewEmailDialog
                                org={organization}
                                accesstoken={accesstoken}
                                accounts={accounts}
                                emailLoginInfo={emailLoginInfo}
                                onEmailRefreshConnection={onEmailRefreshConnection}
                                onEmailLogout={onEmailLogout}
                                initialSubject={cacheSubject || `Request for rates${source?.text ? ` from ${source.text}` : ''}${destination?.text ? ` to ${destination.text}` : ''}`}
                                initialBody={cacheBody || `Hello,<br/><br/>Please advise the rates for the following shipment:<br/><br/>From: ${source?.text ? source.text : ''}<br/>To: ${destination?.text ? destination.text : ''}<br/><pre style="font-family: inherit; font-size: inherit; white-space: pre-wrap;">${productsStr}</pre>Commerce Terms: ${incoterm}<br/><br/>${getEmailClosing({ organization })}.`}
                                subjectEditable={true}
                                bodyFooter={`Internal opportunity Id: #${readableId(opportunity)}<br/>Monada rate request: ${opportunity?.id}`}
                                toRecipients={selectedSuppliers.flatMap(s =>
                                    Array.isArray(s.contacts) && s.contacts.length > 0
                                        ? s.contacts
                                            .filter(contact => contact.email)
                                            .map(contact => {
                                                let name = contactToName({ contact, organization: s.name });
                                                return { name: name, address: contact.email, _group: s.uniqueId || s.name };
                                            })
                                        : [{ name: s.name, address: s.email, _group: s.uniqueId || s.name }]
                                )}
                                ccRecipients={null}
                                onClose={() => setScreen(null)}
                                onSent={onSendRequest}
                                groupBy={true}
                                locked={false}
                                onBack={({ body, subject }) => {
                                    setCacheBody(body);
                                    setCacheSubject(subject);
                                    setScreen('email-picker');
                                }}
                            />
                        </Box>
                    )}
                    {screen === 'email-picker' && (
                        <DialogPicker 
                            suppliers={suppliers}
                            routes={routes}
                            transportationMethod={transportationMethod}
                            initialSelected={selectedSuppliers}
                            initialRoute={selectedRoute}
                            onDone={({ suppliers, route }) => {
                                setSelectedSuppliers(suppliers);
                                if (route !== selectedRoute) {
                                    setCacheBody(null);
                                    setCacheSubject(null);
                                }
                                setSelectedRoute(route);
                                setScreen('email-dialog');
                            }}
                        />
                    )}
                </Dialog>
            )}
            {screen === 'clipboard' && (
                <ApiClipboardDialog
                    onClose={() => setScreen(null)}
                    accesstoken={accesstoken}
                    org={organization}
                    me={me}
                    routes={routes}
                    suppliers={suppliers}
                    transportationMethod={transportationMethod}
                    products={products}
                    setBuyingRates={setBuyingRates}
                    setRequests={setRequests}
                    unlocode={unlocode}
                    seaPorts={seaPorts}
                />
            )}

            {waitingForConversations && (
                <Dialog open={true} onClose={() => {}} maxWidth='md'>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                        <Typography variant='body1' sx={{ textAlign: 'center' }}>
                            Waiting for conversations to be updated...
                        </Typography>
                    </DialogContent>
                </Dialog>
            )}
            <Snackbar
                open={!!sentToast}
                autoHideDuration={6000}
                onClose={() => setSentToast(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ zIndex: 100000001 }}
            >
                <Alert onClose={() => setSentToast(null)} severity='success' variant='filled' sx={{ width: '100%' }}>
                    {sentToast}
                </Alert>
            </Snackbar>
        </Box>
    );
}

function DialogPicker(props) {
    const { suppliers, transportationMethod, routes, onDone, initialSelected, initialRoute } = props;
    const [ selectedSuppliers, setSelectedSuppliers ] = useState(initialSelected || []);
    const [ selectedRoute, setSelectedRoute ] = useState(initialRoute || routes.length > 0 ? routes[0] : null);

    useEffect(() => {
        if (initialRoute) {
            setSelectedRoute(initialRoute);
        }
    }, [initialRoute]);

    useEffect(() => {
        if (initialSelected) {
            setSelectedSuppliers(initialSelected);
        }
    }, [initialSelected]);

    function _onDone() {
        onDone({
            suppliers: selectedSuppliers, 
            route: selectedRoute
        });
    }

    return (
        <Box sx={{ padding: '0px 12px 12px 12px', width: '800px' }}>
            <Box sx={{ mb: 2 }}>
                <RouteSelector routes={routes} selectedRoute={selectedRoute} setSelectedRoute={setSelectedRoute} />
            </Box>
            <SidePaneRfqsSupplierSelection suppliers={suppliers} selectedSuppliers={selectedSuppliers} setSelectedSuppliers={setSelectedSuppliers} transportationMethod={transportationMethod} selectedRoute={selectedRoute} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                <Button
                    disabled={selectedSuppliers.length === 0 || !selectedRoute}
                    variant='contained'
                    color='primary'
                    onClick={_onDone}
                >
                    Save and Preview
                </Button>
            </Box>
        </Box>
    );
}

export default SidePaneRfqs;

function contactToName({ contact, organization }) {
    let name = '';
    if (contact.firstName) {
        name += contact.firstName;
    }
    if (contact.lastName) {
        name += ' ' + contact.lastName;
    }
    if (contact.title && organization) {
        name += ' (' + contact.title + ' at ';
    } else if (contact.title) {
        name += ' (' + contact.title + ')';
    } else if (name && organization) {
        name += ' (';
    }
    if (name && organization) {
        name += organization + ')';
    } else if (organization) {
        name = organization;
    }
    return name;
}